import React from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import cssNewWorld from '@/css/module/new-world.css';
import sidebar from '@/css/module/sidebar.css';
import flex from '@/css/module/flex.css';
import { openModal, tableSelected } from '@/actions';
import { START_NEW_QUERY_BUTTON } from '@/constants/test-ids';
import TableRef from '@/util/TableRef';
import { StartFromSavedQueryDropdown } from '@/features/startFromSavedQuery/StartFromSavedQueryDropdown';
import { Button } from '@trevor/ui-components';
import { userAction } from '@/util/helpers/ajax-helpers';
import { useGetCreditConsumptionWarningQuery } from '@/features/credits/apiSlice';
import { useDatasourceId } from '@/hooks';
import {
  selectGetStarted,
  setGetStarted,
  setLastVisitedURL,
} from '@/features/query-builder/queryBuilderSlice';
import { getUserActions } from '@/store-selectors';
import { useSelector } from 'react-redux';
import ModalSqlEditor from '../modals/ModalSqlEditor';
import ModalCsvUpload from '../modals/ModalCsvUpload';
import TablesDropdown from '../TablesDropdown';
import HighlightFeature, {
  BOTTOM_RIGHT,
  userIsFinishedOnboarding,
} from '../HighlightFeature';
import { StartFromButtonsContainer } from '../StartFromButtonsContainer';
import { AvailableButtons } from './types';
import {
  CSV_BUTTON,
  SAVED_QUERIES_BUTTON,
  SQL_BUTTON,
  TABLES_BUTTON,
} from './buttons.constants';

export type StartNewQueryProps = {
  availableButtons: AvailableButtons[];
  sidebarOpen: boolean;
  width?: number;
};

const StartNewQuery: React.FC<StartNewQueryProps> = ({
  availableButtons,
  sidebarOpen,
  width,
}) => {
  const dispatch = useAppDispatch();
  const datasourceId = useDatasourceId();
  const getStarted = useAppSelector(selectGetStarted);
  const userActions = useSelector(getUserActions);
  // The 'start new query' tooltip only appears after the warning query api call to avoid banner overlaps.
  const { isLoading } = useGetCreditConsumptionWarningQuery(datasourceId);

  const onStartNewQueryClick = () => {
    userAction('use-start-new-query');
    dispatch(setGetStarted(false));
    dispatch(setLastVisitedURL(null));
  };

  const onSqlClicked = () => dispatch(openModal(ModalSqlEditor));

  const onCsvClicked = () => dispatch(openModal(ModalCsvUpload));

  const handleTableClicked = (table: TableRef) => (e: React.MouseEvent) => {
    e.preventDefault();
    userAction('use-table');
    dispatch(tableSelected(table));
  };

  const pulsateClass = getStarted ? cssNewWorld.notification : '';

  return (
    <div
      className={sidebar.leftSectionInner}
      style={{ width: `${width}px` }}
      data-quickfix-remove-on-refactoring
    >
      <div className={sidebar.title} data-quickfix-2-remove-on-refactoring>
        <div className={sidebar.titleControl}>
          <Button
            variant="secondary"
            onClick={onStartNewQueryClick}
            className={`position-relative ${pulsateClass}`}
            disabled={!getStarted}
            data-test-id={START_NEW_QUERY_BUTTON}
          >
            Start new query
            {!getStarted || isLoading ? null : (
              <HighlightFeature
                position={BOTTOM_RIGHT}
                text={
                  <span>
                    Click here to <b>start a new query</b>
                  </span>
                }
                hideButton
                delay={500}
                style={{ visibility: 1 }}
              />
            )}
          </Button>
        </div>
        <div className={`${flex.column} margin-right-5`}>
          <div className={flex.alignItemsCenter}>
            <h4
              className="panel-title sidebar-header margin-right-5"
              style={{ marginTop: '0px' }}
            >
              {getStarted ? '0 QUERY STEPS' : '1 QUERY STEP'}
            </h4>
          </div>
        </div>
      </div>
      {getStarted ? null : (
        <div className={`${sidebar.layer} ${sidebar.layerStart}`}>
          <div className={`${sidebar.layerInner} ${flex.alignItemsCenter}`}>
            <div className={sidebar.stepNumberContainer}>
              <div className={sidebar.stepNumber}>1</div>
            </div>
            <div className={sidebar.layerTypeContainer}>
              <div className={sidebar.layerType}>start from</div>
            </div>
            <StartFromButtonsContainer>
              {availableButtons.includes(TABLES_BUTTON) && (
                <TablesDropdown
                  onClick={handleTableClicked}
                  sidebarOpen={sidebarOpen}
                />
              )}
              {userIsFinishedOnboarding(userActions) && (
                <>
                  {availableButtons.includes(SQL_BUTTON) && (
                    <button onClick={onSqlClicked}>SQL</button>
                  )}
                  {availableButtons.includes(CSV_BUTTON) && (
                    <button onClick={onCsvClicked}>CSV</button>
                  )}
                  {availableButtons.includes(SAVED_QUERIES_BUTTON) && (
                    <StartFromSavedQueryDropdown />
                  )}
                </>
              )}
            </StartFromButtonsContainer>
          </div>
        </div>
      )}
      <div className={sidebar.buffer} />
    </div>
  );
};

export default StartNewQuery;
